'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@/comps/primitives/button';
import { NebulyPageStatusPanel } from '@/domains/pages/components/nebuly-page-status-panel';
import { SegmentDimensionMappingTrigger } from '@/domains/pages/shared/segment-dimension-mapping-trigger';
import { getSegmentEmptyCopy, getSegmentEmptyDescription, getSegmentEmptyTitle, parseNoSegmentsEmptyReason, SEGMENT_DIMENSION_DOCS_URL, type SegmentEmptyReport } from '@/domains/pages/ai-proficiency/utils/segment-empty-state';
import { pageKeyToSegmentKind, type SegmentKind } from '@/domains/pages/ai-proficiency/utils/segment-api-payloads';
import type { NebulyPageKey } from '@/domains/pages/types';
import { ExternalLink, Users } from 'lucide-react';
import { useMemo } from 'react';
type SegmentDimensionEmptyStateProps = {
  pageKey: NebulyPageKey;
  reason?: string;
  report?: SegmentEmptyReport;
};
function resolveSegmentKind(pageKey: NebulyPageKey, reason?: string): SegmentKind | null {
  return parseNoSegmentsEmptyReason(reason) ?? pageKeyToSegmentKind(pageKey);
}
export function SegmentDimensionEmptyState(t0) {
  const $ = _c(25);
  const {
    pageKey,
    reason,
    report: t1
  } = t0;
  const report = t1 === undefined ? "proficiency" : t1;
  let t2;
  if ($[0] !== pageKey || $[1] !== reason) {
    t2 = resolveSegmentKind(pageKey, reason);
    $[0] = pageKey;
    $[1] = reason;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const segmentKind = t2;
  if (!segmentKind) {
    return null;
  }
  let t3;
  if ($[3] !== segmentKind) {
    t3 = getSegmentEmptyCopy(segmentKind);
    $[3] = segmentKind;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const copy = t3;
  let t4;
  if ($[5] !== report || $[6] !== segmentKind) {
    t4 = getSegmentEmptyTitle(segmentKind, report);
    $[5] = report;
    $[6] = segmentKind;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const title = t4;
  let t5;
  if ($[8] !== report || $[9] !== segmentKind) {
    t5 = getSegmentEmptyDescription(segmentKind, report);
    $[8] = report;
    $[9] = segmentKind;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const description = t5;
  const t6 = `nebuly-page-empty-segments-${segmentKind}`;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Users className="size-10 text-muted-foreground" strokeWidth={1.5} />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ExternalLink className="size-3.5" />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== copy.docsCtaLabel) {
    t9 = <Button variant="defaultDark" size="sm" className="h-8 gap-1.5 rounded-full px-4 text-xs" asChild={true}><a href={SEGMENT_DIMENSION_DOCS_URL} target="_blank" rel="noopener noreferrer">{t8}{copy.docsCtaLabel}</a></Button>;
    $[13] = copy.docsCtaLabel;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== segmentKind) {
    t10 = <SegmentDimensionMappingTrigger segmentKind={segmentKind} />;
    $[15] = segmentKind;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== description || $[21] !== t11 || $[22] !== t6 || $[23] !== title) {
    t12 = <NebulyPageStatusPanel data-testid={t6} icon={t7} title={title} description={description} actions={t11} />;
    $[20] = description;
    $[21] = t11;
    $[22] = t6;
    $[23] = title;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}